import * as React from 'react';
import Table from 'rsuite/Table';
import { CachedLotNoData } from '../../../store/run-result-common-types';
import { ProcessedGriffolsRunResultRow } from '../../../store/RunResultsGriffols';
import stringOrderingAlphabet from '../../../utils/orderStringsAlphabetically';
import { CellDataConfig } from '../custom-cells/cellUtils';
import { DateCell } from '../custom-cells/DateCell';
import { EditCell } from '../custom-cells/EditCell';
import { GriffolsLabelsCell } from '../custom-cells/GriffolsLabelsCell';
import { GriffolsRunDataCell } from '../custom-cells/GriffolsRunDataCell';
import { getTableDimensions, STANDARD_CELL_WIDTH, TABLE_TYPE } from '../utils/tableSizeUtils';
import { COUNTER_OPERATION } from '../utils/types';

const COLUMN_MIN_WIDTH = 150;
const SUBCOLUMN_WIDTH = 80;
const COLUMN_PADDING = 10;

export type EditModeToggleCallback = (
    rowId: string,
    isEditModeOn: boolean
) => void;

export type NewLotNoInsertionToggleCallback = (
    rowId: string,
    operation: COUNTER_OPERATION
) => void;

export type GriffolsTableRowData = ProcessedGriffolsRunResultRow & {
    isInEditMode: boolean;
    isLastRunOfTheDay: boolean;
    newLotNoInsertionsCount: number;
    id: string;
    toggleEditMode: EditModeToggleCallback;
};

const createDynamicHeaderSection = (
    headerData: string[],
    subHeaderData: string[],
    cellDataConfig: CellDataConfig,
    collectUpdatedDataForRow: any,
    getInhouseBatchOptions: any,
    toggleNewLotNoInsertionForRow: NewLotNoInsertionToggleCallback,
    cachedNewLotNoData: CachedLotNoData | null
): any[] => {
    let headers: any[] = [];
    let sortedHeaderData = headerData?.sort(stringOrderingAlphabet);
    let subcolumnsCount = subHeaderData?.length;
    let columnWidth = Math.max(COLUMN_MIN_WIDTH, subcolumnsCount * SUBCOLUMN_WIDTH) + COLUMN_PADDING * 2;

    sortedHeaderData.forEach((equipmentName: string) => {
        headers.push(
            <Table.Column width={columnWidth} key={equipmentName}>
                <Table.HeaderCell>{equipmentName}</Table.HeaderCell>
                <GriffolsRunDataCell
                    dataKey={equipmentName}
                    cellDataConfig={cellDataConfig}
                    collectUpdatedDataForRow={collectUpdatedDataForRow}
                    getInhouseBatchOptions={getInhouseBatchOptions}
                    toggleNewLotNoInsertionForRow={toggleNewLotNoInsertionForRow}
                    cachedNewLotNoData={cachedNewLotNoData}
                />
            </Table.Column>
        );
    });
    headers.push(
        <Table.Column  flexGrow={1}>
            <Table.HeaderCell>&nbsp;</Table.HeaderCell>
            <Table.Cell />
        </Table.Column>
    );

    return headers;
};

const createGriffolsTable = (
    headerData: string[],
    subHeaderData: string[],
    data: GriffolsTableRowData[],
    cellDataConfig: CellDataConfig,
    toggleEditModeForRow: EditModeToggleCallback,
    rowKey: string,
    handleExpanded: (rowId: string) => void,
    expandedRowKeys: any,
    collectUpdatedDataForRow: any,
    getInhouseBatchOptions: any,
    toggleNewLotNoInsertionForRow: NewLotNoInsertionToggleCallback,
    addRunAfter: (uuid: string, rowModelId: string) => void,
    removeRun: (uuid: string) => void,
    cachedNewLotNoData: CachedLotNoData | null
) => {
    const { viewModeRowHeight, editModeRowHeight } =
        getTableDimensions(TABLE_TYPE.GRIFOLS, cellDataConfig, 2);

    return (
        <Table
            rowHeight={(rowData: GriffolsTableRowData) => rowData?.isInEditMode ? editModeRowHeight : viewModeRowHeight}
            rowKey={rowKey}
            data={data}
            className="run-results-grid"
            shouldUpdateScroll={true}
            autoHeight={true}
            hover={false}
        >
            <Table.Column width={180} key="edit_cell" fixed={true}>
                <Table.HeaderCell>&nbsp;</Table.HeaderCell>
                <EditCell
                    toggleEditModeForRow={toggleEditModeForRow}
                    handleExpanded={handleExpanded}
                    toggleNewLotNoInsertionForRow={toggleNewLotNoInsertionForRow}
                    addRunAfter={addRunAfter}
                    removeRun={removeRun}
                />
            </Table.Column>
            <Table.Column width={STANDARD_CELL_WIDTH} fixed={true}>
                <Table.HeaderCell>Day</Table.HeaderCell>
                <Table.Cell dataKey="day" />
            </Table.Column>

            <Table.Column width={STANDARD_CELL_WIDTH} fixed={true}>
                <Table.HeaderCell>Date</Table.HeaderCell>
                <DateCell dataKey="date" />
            </Table.Column>

            <Table.Column width={STANDARD_CELL_WIDTH} fixed={true}>
                <Table.HeaderCell>Run</Table.HeaderCell>
                <Table.Cell dataKey="run" />
            </Table.Column>

            <Table.Column width={STANDARD_CELL_WIDTH * 3 + 15} fixed={true}>
                <Table.HeaderCell>&nbsp;</Table.HeaderCell>
                <GriffolsLabelsCell cellDataConfig={cellDataConfig}/>
            </Table.Column>

            {createDynamicHeaderSection(headerData, subHeaderData, cellDataConfig, collectUpdatedDataForRow, getInhouseBatchOptions, toggleNewLotNoInsertionForRow, cachedNewLotNoData)}
        </Table>
    ); //TODO: cleanup console errors (props to send e.g. handleExpanded)
};

export default createGriffolsTable;
